export type FlowUiStatus = 'paused' | 'testing' | 'sending';

export type FlowStatusInfo = {
    status: FlowUiStatus;
    label: string;
    hint: string;
    badgeVariant: 'neutral' | 'warning' | 'success';
};

const SENT_STATUSES = new Set(['sent', 'delivered', 'opened', 'clicked']);

export type ChannelId = 'email' | 'push' | 'in_app';

export type ChannelTotals = {
    channel: ChannelId;
    label: string;
    sent: number;
    opened: number;
    suppressed: number;
    dryRun: number;
};

export type DeliveryLike = {
    channel: string;
    status: string;
    n: number;
};

const CHANNEL_META: { id: ChannelId; label: string; aliases: string[] }[] = [
    { id: 'email', label: 'Email', aliases: ['email'] },
    { id: 'push', label: 'Push', aliases: ['push'] },
    { id: 'in_app', label: 'En la app', aliases: ['in_app', 'in-app', 'inapp'] },
];

export function sanitizeCommKey(raw: string): string {
    return raw.toLowerCase().replace(/[^a-z0-9_.]/g, '');
}

export function isRegistrationTemplate(key: string): boolean {
    return key.startsWith('activacion_');
}

export function registrationPieceLabel(key: string): string {
    const known = {
        'activacion_a0.subject.principiante': 'Asunto · principiante',
        'activacion_a0.subject.intermedio': 'Asunto · intermedio',
        'activacion_a0.subject.experto': 'Asunto · experto',
        'activacion_a0.peticion.principiante': 'Petición · principiante',
        'activacion_a0.peticion.intermedio': 'Petición · intermedio',
        'activacion_a0.peticion.experto': 'Petición · experto',
        'activacion_a0.contenido.salada': 'Contenido · agua salada',
        'activacion_a0.contenido.dulce': 'Contenido · agua dulce',
        'activacion_a0.contenido.ambas': 'Contenido · ambas',
        'activacion_a2.tecnica': 'Técnica',
    } as Record<string, string>;
    return known[key] ?? key;
}

export function isRegistrationPiece(key: string, channel: string): boolean {
    return channel === 'fragment' || channel === 'subject' || key.includes('.');
}

export function senderLabel(provider: string | null | undefined): string {
    if (provider === 'acs') return 'Alberto M. · alberto@news.aipeces.com';
    if (provider === 'graph') return 'Alberto M. · hola@aipeces.com';
    if (provider === 'fcm') return 'Aviso en el móvil';
    if (!provider) return '—';
    return provider;
}

export function templateSubject(row: { subject?: string | null; title?: string | null }): string {
    return (row.subject || row.title || '').trim();
}

export type TemplateDraft = {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string;
    title: string;
    body: string;
    preheader: string;
    click_url: string;
    is_active: boolean;
};

export function emptyTemplateDraft(): TemplateDraft {
    return {
        key: '',
        channel: 'email',
        locale: 'es',
        variant: 'A',
        subject: '',
        title: '',
        body: '',
        preheader: '',
        click_url: '',
        is_active: true,
    };
}

export function draftFromTemplate(t: {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string | null;
    title: string | null;
    body: string;
    preheader: string | null;
    click_url: string | null;
    is_active: boolean;
}): TemplateDraft {
    const subject = templateSubject(t);
    return {
        key: t.key,
        channel: t.channel,
        locale: t.locale,
        variant: t.variant,
        subject,
        title: subject,
        body: t.body,
        preheader: t.preheader || '',
        click_url: t.click_url || '',
        is_active: t.is_active,
    };
}

export function formatUnknownError(error: unknown, fallback: string): string {
    if (!error) return fallback;
    if (error instanceof Error && error.message.trim()) return error.message;
    if (typeof error === 'object' && error && 'message' in error) {
        const message = String((error as { message: unknown }).message ?? '').trim();
        if (message) return message;
    }
    if (typeof error === 'string' && error.trim()) return error;
    return fallback;
}

export function resolveFlowStatus(
    journey: { is_active: boolean; dry_run: boolean },
    emailDryRun: boolean | undefined
): FlowStatusInfo {
    if (!journey.is_active) {
        return {
            status: 'paused',
            label: 'Pausado',
            hint: 'Este flujo no corre, aunque el motor esté encendido.',
            badgeVariant: 'neutral',
        };
    }

    const flowTesting = journey.dry_run;
    const globalTesting = emailDryRun === true;
    if (flowTesting || globalTesting) {
        let hint = 'En pruebas (este flujo)';
        if (flowTesting && globalTesting) hint = 'En pruebas (este flujo + email global)';
        else if (globalTesting) hint = 'En pruebas (email global)';
        return {
            status: 'testing',
            label: 'En pruebas',
            hint,
            badgeVariant: 'warning',
        };
    }

    return {
        status: 'sending',
        label: 'Mandando',
        hint: 'Mandando · el email puede salir de verdad.',
        badgeVariant: 'success',
    };
}

export function formatStepTiming(delayHours: number, windowHours: number): string {
    const delay = Math.round(Number(delayHours) || 0);
    const window = Math.round(Number(windowHours) || 0);
    const day = Math.round(delay / 24);
    let when: string;
    if (delay <= 0) when = 'Al instante';
    else if (delay < 24) when = `+${delay} h`;
    else when = `Día ${day} (+${delay} h)`;
    return `${when} · margen ${window} h`;
}

export function delayToDayLabel(delayHours: number): string {
    const delay = Math.round(Number(delayHours) || 0);
    if (delay <= 0) return 'sale al instante';
    if (delay < 24) return `sale a las ${delay} h`;
    return `equivale al día ${Math.round(delay / 24)}`;
}

export function buildChannelTotals(stats: DeliveryLike[]): ChannelTotals[] {
    return CHANNEL_META.map((meta) => {
        const rows = stats.filter((s) => meta.aliases.includes((s.channel || '').toLowerCase()));
        const sent = rows
            .filter((s) => SENT_STATUSES.has(s.status))
            .reduce((acc, s) => acc + Number(s.n), 0);
        const opened = rows
            .filter((s) => s.status === 'opened' || s.status === 'clicked')
            .reduce((acc, s) => acc + Number(s.n), 0);
        const suppressed = rows
            .filter((s) => s.status === 'suppressed')
            .reduce((acc, s) => acc + Number(s.n), 0);
        const dryRun = rows
            .filter((s) => s.status === 'dry_run')
            .reduce((acc, s) => acc + Number(s.n), 0);
        return { channel: meta.id, label: meta.label, sent, opened, suppressed, dryRun };
    });
}

export function overlappingSignals(noneSignals: string[], allSignals: string[]): string[] {
    return noneSignals.filter((key) => allSignals.includes(key));
}
